// ════════════════════════════════════════════════════════════════════════
//  Teilnahmeurkunde als PDF
//
//  Eine Seite, x-mal ausgedruckt, den Namen schreibt die Lehrkraft von Hand
//  darauf – dasselbe Verfahren wie beim Kärtchen und aus demselben Grund: der
//  Name des Kindes steht nicht auf der Plattform und soll es auch nicht.
//
//  Jedes Kind bekommt eine Urkunde. Es gibt kein „bestanden“, keine Note und
//  keinen Vergleich mit anderen Kindern; wer mitgemacht hat, hat teilgenommen.
// ════════════════════════════════════════════════════════════════════════

import {
  PDFDocument,
  StandardFonts,
  type PDFEmbeddedPage,
  type PDFFont,
  type PDFPage,
} from "pdf-lib";
import {
  A4,
  farben,
  fileNamePart,
  sizeToFit,
  toWinAnsi,
} from "../../../shared/lib/pdf.js";
import type { StudentSummary } from "./summary.js";
import { embedOtto } from "../../../shared/lib/otto.js";

/** Seitenrand der Urkunde: breiter als beim Kärtchen, sie hängt an der Wand. */
const RAND = 64;

/**
 * Ein Kind auf seiner eigenen Urkunde – erkennbar am Übungscode, nicht am Namen.
 *
 * Der Code sagt der Lehrkraft, welches Blatt zu wem gehört; den Namen schreibt
 * sie auf die Linie. Damit bleibt auch die persönliche Urkunde datensparsam.
 */
export type CertificateStudent = {
  readonly code: string;
  /** Null, wenn dieses Kind in diesem Lehrgang nichts geübt hat. */
  readonly summary: StudentSummary | null;
};

export type CertificateInput = {
  readonly schoolClass: string;
  readonly schoolYear: string;
  readonly schoolName: string;
  readonly courseTitle: string;
  readonly courseSubtitle?: string | null;
  /**
   * Leer oder nicht gesetzt: EINE Urkunde für die ganze Klasse, zum
   * Vervielfältigen. Sonst eine Seite je Kind.
   */
  readonly students?: readonly CertificateStudent[] | undefined;
};

/** Text mittig auf der Seite, verkleinert wenn er sonst über den Rand liefe. */
function centered(
  page: PDFPage,
  text: string,
  options: {
    readonly y: number;
    readonly size: number;
    readonly font: PDFFont;
    readonly color: (typeof farben)[keyof typeof farben];
    readonly min?: number;
  },
): void {
  const clean = toWinAnsi(text);
  const width = A4.breite - 2 * RAND;
  const size = sizeToFit(options.font, clean, width, options.size, options.min ?? 8);
  page.drawText(clean, {
    x: (A4.breite - options.font.widthOfTextAtSize(clean, size)) / 2,
    y: options.y,
    size,
    font: options.font,
    color: options.color,
  });
}

/** Eine Linie zum Beschriften, mit Beschriftung darunter. */
function signatureLine(
  page: PDFPage,
  label: string,
  options: {
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly font: PDFFont;
  },
): void {
  page.drawLine({
    start: { x: options.x, y: options.y },
    end: { x: options.x + options.width, y: options.y },
    thickness: 0.75,
    color: farben.grey,
  });
  page.drawText(toWinAnsi(label), {
    x: options.x,
    y: options.y - 12,
    size: 9,
    font: options.font,
    color: farben.quiet,
  });
}

export async function createCertificatePdf(input: CertificateInput): Promise<Uint8Array> {
  const doc = await PDFDocument.create();
  doc.setTitle(`Urkunde ${input.schoolClass} ${input.schoolYear}`);
  doc.setCreator("Otto tippt los");

  const normal = await doc.embedFont(StandardFonts.Helvetica);
  const fett = await doc.embedFont(StandardFonts.HelveticaBold);

  const otto = await embedOtto(doc, 74);

  // Ohne Kinderliste eine Seite zum Vervielfältigen, sonst eine je Kind.
  const sheets: (CertificateStudent | null)[] =
    input.students && input.students.length > 0 ? [...input.students] : [null];

  for (const student of sheets) drawSheet(doc, input, student, normal, fett, otto);

  return doc.save();
}

function drawSheet(
  doc: PDFDocument,
  input: CertificateInput,
  student: CertificateStudent | null,
  normal: PDFFont,
  fett: PDFFont,
  otto: PDFEmbeddedPage,
): void {
  const page = doc.addPage([A4.breite, A4.hoehe]);
  const mitte = A4.breite / 2;

  centered(page, "Otto tippt los", {
    y: A4.hoehe - 96,
    size: 11,
    font: normal,
    color: farben.quiet,
  });
  centered(page, "Urkunde", {
    y: A4.hoehe - 160,
    size: 44,
    font: fett,
    color: farben.ink,
  });
  centered(page, "für das Tastaturschreiben", {
    y: A4.hoehe - 186,
    size: 13,
    font: normal,
    color: farben.quiet,
  });

  // Otto statt Zierrat. Sterne standen hier einmal - in diesem Lehrgang sind
  // sie aber eine Bewertung (eine bis drei je Stufe), und drei davon
  // behaupteten ein Ergebnis, das die Urkunde gar nicht kennt.
  page.drawPage(otto, { x: mitte - 37, y: A4.hoehe - 296, width: 74, height: 74 });

  // Die Namenslinie: hier entsteht die Zuordnung, mit einem Stift, bei der
  // Lehrkraft. Wie auf dem Kärtchen.
  signatureLine(page, "Name", {
    x: RAND + 60,
    y: A4.hoehe - 316,
    width: A4.breite - 2 * (RAND + 60),
    font: normal,
  });

  centered(page, "hat am Lehrgang", {
    y: A4.hoehe - 376,
    size: 13,
    font: normal,
    color: farben.quiet,
  });
  centered(page, input.courseTitle, {
    y: A4.hoehe - 410,
    size: 22,
    font: fett,
    color: farben.ink,
  });
  if (input.courseSubtitle) {
    centered(page, input.courseSubtitle, {
      y: A4.hoehe - 432,
      size: 12,
      font: normal,
      color: farben.quiet,
    });
  }
  // „teilgenommen“, nicht „erfolgreich abgeschlossen“: es ist eine
  // TeilnahmeUrkunde, und auf der persönlichen Form steht darunter „42 von 48
  // Übungen geübt“. Ein Abschluss zu behaupten, den die Zeile daneben
  // widerlegt, wäre die eine Stelle, an der ein Kind die Urkunde nicht glaubt.
  centered(page, "teilgenommen.", {
    y: A4.hoehe - 470,
    size: 13,
    font: normal,
    color: farben.quiet,
  });

  centered(page, `${input.schoolClass} · Schuljahr ${input.schoolYear}`, {
    y: A4.hoehe - 522,
    size: 13,
    font: normal,
    color: farben.ink,
  });
  centered(page, input.schoolName, {
    y: A4.hoehe - 542,
    size: 12,
    font: normal,
    color: farben.quiet,
  });

  if (student !== null) {
    // Oben rechts, klein: beim Durchblättern eines Stapels sucht die Lehrkraft
    // genau hier.
    const code = toWinAnsi(student.code);
    page.drawText(code, {
      x: A4.breite - RAND - normal.widthOfTextAtSize(code, 10),
      y: A4.hoehe - 96,
      size: 10,
      font: normal,
      color: farben.quiet,
    });

    // Fehlt der Stand, fällt die Zeile weg – ein Strich läse sich wie ein Mangel
    // des Kindes. Und es steht kein Vergleich mit anderen Kindern darauf.
    if (student.summary !== null) {
      const { stagesDone, stagesTotal, stars, speed } = student.summary;

      if (stagesDone > 0) {
        centered(page, `${stagesDone} von ${stagesTotal} Übungen geübt`, {
          y: A4.hoehe - 600,
          size: 13,
          font: normal,
          color: farben.ink,
        });
        centered(page, `${stars} Sterne gesammelt`, {
          y: A4.hoehe - 622,
          size: 12,
          font: normal,
          color: farben.quiet,
        });
      }

      // Nur wenn BEIDE Messungen da sind. „Am Anfang 6“ allein sagt einem Kind,
      // wo es angefangen hat, und verschweigt genau das, wofür die Zeile da ist.
      if (speed?.before != null && speed.after != null) {
        centered(
          page,
          `Am Anfang ${speed.before} Wörter pro Minute · jetzt ${speed.after}`,
          {
            y: A4.hoehe - (stagesDone > 0 ? 656 : 600),
            size: 13,
            font: fett,
            color: farben.ink,
          },
        );
      }
    }
  }

  const lineWidth = (A4.breite - 2 * RAND - 40) / 2;
  signatureLine(page, "Ort, Datum", { x: RAND, y: 150, width: lineWidth, font: normal });
  signatureLine(page, "Unterschrift", {
    x: A4.breite - RAND - lineWidth,
    y: 150,
    width: lineWidth,
    font: normal,
  });
}

/**
 * Dateiname mit Klasse und Schuljahr – im Downloadordner sonst nicht zu finden.
 *
 * Einzahl oder Mehrzahl, damit die beiden Ausdrucke im Ordner auseinanderzuhalten
 * sind: das eine Blatt zum Vervielfältigen, der Stapel mit den Codes.
 */
export function certificateFileName(
  schoolClass: string,
  schoolYear: string,
  perStudent = false,
  singleCode?: string,
): string {
  // Eine einzelne Urkunde trägt den Code im Namen. Im Downloadordner liegen
  // sonst drei Dateien „urkunden-3a-2026-27.pdf“, und welche das Kind meint,
  // das gestern gefehlt hat, weiß niemand mehr.
  if (singleCode !== undefined) return `urkunde-${fileNamePart(singleCode)}.pdf`;
  const wort = perStudent ? "urkunden" : "urkunde";
  return `${wort}-${fileNamePart(schoolClass)}-${fileNamePart(schoolYear)}.pdf`;
}
